import { CircleAlert, CircleCheck, CircleHelp } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { useT } from '@/lib/i18n/useT';

type CapacityStatus = 'green' | 'yellow' | 'red' | 'pending';

interface CapacityIndicatorProps {
  status: CapacityStatus;
  expectedCaptures?: number;
  inventoryCount?: number;
}

/**
 * PRD Xotski v2.0 §16 / §19 — capacity planning indicator on Project
 * Environment.
 *
 * Status logic (PRD-locked):
 *   - green   inventory ≥ expected × 1.15  (15 % buffer)
 *   - yellow  inventory between expected × 1.00 and 1.15
 *   - red     inventory < expected × 1.00
 *   - pending we don't yet have the historical capture rate needed to
 *             plug the formula:
 *               BAs × QR posters × hours × rate = expected captures
 *             The Atledi backend (Phase 4) provides the rate; until then
 *             we surface a "pending" state and the CTA to run the manual
 *             check. No fake number is shown.
 */
export function CapacityIndicator({
  status,
  expectedCaptures,
  inventoryCount,
}: CapacityIndicatorProps) {
  const t = useT();
  const meta = META[status];

  return (
    <Card
      className="border p-4"
      style={{ borderColor: meta.borderVar, backgroundColor: meta.bgVar }}
    >
      <div className="flex items-start gap-3">
        <span style={{ color: meta.iconVar }} className="mt-0.5">
          {meta.icon}
        </span>
        <div className="flex-1">
          <p
            className="text-[10px] font-semibold uppercase tracking-wider"
            style={{ color: meta.iconVar }}
          >
            {t('project.capacity.label')} · {t(meta.statusKey)}
          </p>
          <p className="mt-0.5 text-sm text-[var(--color-fg-primary)]">
            {status === 'pending'
              ? t('project.capacity.pending')
              : buildSummary(t, status, expectedCaptures, inventoryCount)}
          </p>
        </div>
      </div>
    </Card>
  );
}

const META: Record<
  CapacityStatus,
  {
    icon: React.ReactNode;
    iconVar: string;
    bgVar: string;
    borderVar: string;
    statusKey: string;
  }
> = {
  green: {
    icon: <CircleCheck className="h-5 w-5" />,
    iconVar: 'var(--color-success)',
    bgVar:
      'color-mix(in srgb, var(--color-success) 6%, var(--color-bg-surface))',
    borderVar:
      'color-mix(in srgb, var(--color-success) 30%, var(--color-border-subtle))',
    statusKey: 'project.capacity.green',
  },
  yellow: {
    icon: <CircleAlert className="h-5 w-5" />,
    iconVar: 'var(--color-warn)',
    bgVar:
      'color-mix(in srgb, var(--color-warn) 6%, var(--color-bg-surface))',
    borderVar:
      'color-mix(in srgb, var(--color-warn) 30%, var(--color-border-subtle))',
    statusKey: 'project.capacity.yellow',
  },
  red: {
    icon: <CircleAlert className="h-5 w-5" />,
    iconVar: 'var(--color-error)',
    bgVar:
      'color-mix(in srgb, var(--color-error) 6%, var(--color-bg-surface))',
    borderVar:
      'color-mix(in srgb, var(--color-error) 30%, var(--color-border-subtle))',
    statusKey: 'project.capacity.red',
  },
  pending: {
    icon: <CircleHelp className="h-5 w-5" />,
    iconVar: 'var(--color-fg-muted)',
    bgVar: 'var(--color-bg-surface)',
    borderVar: 'var(--color-border-subtle)',
    statusKey: 'project.capacity.pendingShort',
  },
};

function buildSummary(
  t: (k: string) => string,
  status: CapacityStatus,
  expected?: number,
  inventory?: number,
): string {
  if (expected == null || inventory == null) return t('project.capacity.pending');
  return t(`project.capacity.summary.${status}`)
    .replace('{expected}', String(expected))
    .replace('{inventory}', String(inventory));
}

/**
 * PRD §22 — capacity formula.
 *
 *   expectedCaptures = BAs × QR posters × hours × rate
 *
 * `rate` is the historical capture rate per BA-hour per QR poster from
 * Atledi (Phase 4 backend). Until the rate ships we accept it as a
 * caller-provided override and return 'pending' when it's missing or 0
 * (we never invent a rate — PRD §22 is explicit about that).
 */
export function computeCapacity(input: {
  bas: number;
  qrPosters: number;
  hours: number;
  ratePerBaHourPerQr: number | null;
  inventory: number;
}): { status: CapacityStatus; expectedCaptures: number | null } {
  const { bas, qrPosters, hours, ratePerBaHourPerQr, inventory } = input;
  if (!ratePerBaHourPerQr || ratePerBaHourPerQr <= 0) {
    return { status: 'pending', expectedCaptures: null };
  }
  const expected = Math.max(
    0,
    Math.round(bas * qrPosters * hours * ratePerBaHourPerQr),
  );
  if (expected === 0) return { status: 'pending', expectedCaptures: 0 };
  const ratio = inventory / expected;
  if (ratio >= 1.15) return { status: 'green', expectedCaptures: expected };
  if (ratio >= 1.0) return { status: 'yellow', expectedCaptures: expected };
  return { status: 'red', expectedCaptures: expected };
}
